import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import {
  ColumnVisibilityKeyType,
  setColumnVisibility as setPersistedColumnVisibility,
} from '@brands/store/slices/columnVisibilitySlice';

import styles from './caDataGrid.module.scss';

export { styles as caGridStyles };

// These three are calibrated against the cell type below, so they move with it.
// At the design's 16px body (was 13px) a character is about 9.4px rather than
// 7.6px, cells carry 16px of horizontal padding a side rather than 12px, and the
// header grows from roughly 40px to 48px. Leaving them behind would have the
// column auto-sizer under-measure every column and the sticky filter row sit
// under the header.
const CHAR_PX = 9.4;
const COL_PAD_PX = 56;
const COL_MIN_PX = 120;
const COL_MAX_PX = 400;
const HEADER_HEIGHT = 48;

// Stable reference so `visibleColumns` memoization holds when nothing is persisted yet.
const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {};

export function columnWidthPx(label: string, cellStrings: string[]): number {
  const longest = [label, ...cellStrings].reduce((m, s) => Math.max(m, (s ?? '').length), 0);
  return Math.min(COL_MAX_PX, Math.max(COL_MIN_PX, Math.ceil(longest * CHAR_PX + COL_PAD_PX)));
}

export const EllipsisCell = ({ text }: { text: string }) => (
  <Tooltip title={text} placement="top-start" enterDelay={400} disableHoverListener={!text}>
    <Box
      component="span"
      sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {text}
    </Box>
  </Tooltip>
);

// Type and spacing here come from the design system's own staff grid: header
// 16px/700, body 16px/400 in 12px 16px cells, which lands rows at 68px rather
// than the 47px this grid used to draw. The extra height is the point — the
// design trades rows-per-screen for legibility, and matching it was chosen
// deliberately over keeping the denser table.
const headerCellSx = {
  backgroundColor: '#f7f8fa',
  fontSize: 16,
  fontWeight: 700,
  padding: '12px 16px',
};

const bodyCellSx = {
  fontSize: 16,
  fontWeight: 400,
  padding: '12px 16px',
};

export interface ColumnDef<TRow = any> {
  key: string;
  label: string;
  widthPx: number;
  sortable?: boolean;
  renderCell: (row: TRow) => React.ReactNode;
  filterContent?: React.ReactNode;
}

export interface CADataGridProps<TRow = any> {
  columns: ColumnDef<TRow>[];
  rows: TRow[];
  getRowId?: (row: TRow) => string | number;
  onRowClick?: (row: TRow) => void;
  selectedRowId?: number | string | null;
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: (key: string) => void;
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  error?: 'forbidden' | 'generic' | null;
  onRetry?: () => void;
  showManageColumns?: boolean;
  /**
   * When set, the Manage-columns selection is persisted to the columnVisibility
   * Redux slice under this key, so it survives navigating away and back.
   * When omitted, visibility lives in local state and resets on unmount.
   */
  columnVisibilityKey?: ColumnVisibilityKeyType;
  isMobile?: boolean;
  getRowSx?: (row: TRow) => Record<string, unknown>;
}

function CADataGrid<TRow = any>({
  columns,
  rows,
  getRowId,
  onRowClick,
  selectedRowId,
  sortBy,
  sortDirection,
  onSort,
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions = [10, 25, 50, 100],
  onPageChange,
  onRowsPerPageChange,
  loading,
  emptyMessage = 'No results found',
  error,
  onRetry,
  showManageColumns = true,
  columnVisibilityKey,
  isMobile = false,
  getRowSx,
}: CADataGridProps<TRow>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const persistedColumnVisibility = useAppSelector((s) =>
    columnVisibilityKey ? s.columnVisibility[columnVisibilityKey] : undefined,
  );
  const [localColumnVisibility, setLocalColumnVisibility] = useState<Record<string, boolean>>({});

  const columnVisibility = columnVisibilityKey
    ? persistedColumnVisibility ?? EMPTY_COLUMN_VISIBILITY
    : localColumnVisibility;

  const toggleColumn = useCallback(
    (key: string) => {
      // `!== false` is the visible predicate everywhere here, so an unset key
      // counts as visible — toggle off that predicate, not the raw value.
      const next = { ...columnVisibility, [key]: columnVisibility[key] === false };
      if (columnVisibilityKey) {
        dispatch(setPersistedColumnVisibility({ tableKey: columnVisibilityKey, model: next }));
      } else {
        setLocalColumnVisibility(next);
      }
    },
    [columnVisibility, columnVisibilityKey, dispatch],
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => columnVisibility[c.key] !== false),
    [columns, columnVisibility],
  );

  const hasFilterRow = visibleColumns.some((c) => c.filterContent != null);

  return (
    <Box sx={{ width: '100%' }}>
      {error === 'forbidden' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          You do not have permission to view these records.
        </Alert>
      )}
      {error === 'generic' && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            onRetry && (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          Unable to load records. Please try again.
        </Alert>
      )}

      {showManageColumns && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: isMobile ? 0.5 : 1 }}>
          <Button
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            sx={{
              textTransform: 'none',
              ...(isMobile
                ? {
                    color: 'var(--primary-color)',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 15,
                    fontWeight: 500,
                    letterSpacing: '0.46px',
                    lineHeight: '26px',
                    minWidth: 0,
                    p: 0,
                    '& .MuiButton-startIcon': { ml: 0, mr: '6px' },
                    '& svg': { fontSize: 20 },
                  }
                : {}),
            }}
          >
            Manage columns
          </Button>
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box p={2}>
          {columns.map((c) => (
            <Box key={c.key} display="flex" alignItems="center">
              <Checkbox
                checked={columnVisibility[c.key] !== false}
                onChange={() => toggleColumn(c.key)}
              />
              {c.label}
            </Box>
          ))}
        </Box>
      </Popover>

      <Paper
        sx={{
          border: '1px solid #E5E7EB',
          ...(isMobile
            ? { borderColor: '#E0E0E0', borderRadius: '4px', boxShadow: 'none', overflow: 'hidden' }
            : {}),
        }}
      >
        <TableContainer
          sx={{
            width: '100%',
            overflow: 'auto',
            maxHeight: isMobile ? 'calc(100dvh - 282px)' : 'calc(100vh - 260px)',
            ...(isMobile
              ? {
                  scrollbarWidth: 'auto',
                  '&::-webkit-scrollbar': { width: 10, height: 10 },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#7a7a7a', borderRadius: 8 },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#f3f4f6' },
                }
              : {}),
          }}
        >
          <Table
            stickyHeader
            size="medium"
            sx={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}
          >
            <colgroup>
              {visibleColumns.map((c) => (
                <col key={c.key} style={{ width: c.widthPx, minWidth: c.widthPx }} />
              ))}
            </colgroup>

            <TableHead>
              <TableRow>
                {visibleColumns.map((c, i) => (
                  <TableCell
                    key={c.key}
                    sx={{
                      ...headerCellSx,
                      ...(isMobile
                        ? {
                            color: '#263548',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: 15,
                            fontWeight: 600,
                            height: 46,
                            lineHeight: '22px',
                            padding: '11px 8px 11px 10px',
                          }
                        : {}),
                      cursor: onSort && c.sortable !== false ? 'pointer' : 'default',
                      borderRight: i === visibleColumns.length - 1 ? 'none' : '1px solid #E5E7EB',
                      width: c.widthPx,
                      minWidth: c.widthPx,
                      maxWidth: c.widthPx,
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                    }}
                    onClick={() => c.sortable !== false && onSort?.(c.key)}
                  >
                    <Tooltip title={c.label} placement="top-start" enterDelay={500}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        width="100%"
                        sx={{
                          overflow: 'hidden',
                          minWidth: 0,
                          '&:hover .platform-sort-arrow': { opacity: 1, pointerEvents: 'auto' },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {c.label}
                        </Box>
                        {onSort && c.sortable !== false && (
                          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', ml: 'auto' }}>
                            {sortBy === c.key && sortDirection === 'desc' ? (
                              <ArrowDownwardIcon
                                fontSize="small"
                                className="platform-sort-arrow"
                                sx={{ opacity: 1, transition: 'opacity 0.2s ease' }}
                              />
                            ) : (
                              <ArrowUpwardIcon
                                fontSize="small"
                                className="platform-sort-arrow"
                                sx={{
                                  opacity: isMobile || sortBy === c.key ? 1 : 0,
                                  pointerEvents: isMobile || sortBy === c.key ? 'auto' : 'none',
                                  transition: 'opacity 0.2s ease',
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>

              {hasFilterRow && (
                <TableRow>
                  {visibleColumns.map((c) => (
                    <TableCell
                      key={c.key}
                      sx={{
                        background: '#fff',
                        padding: '4px 8px',
                        ...(isMobile ? { height: 48, padding: '8px' } : {}),
                        borderRight: '1px solid #E5E7EB',
                        width: c.widthPx,
                        minWidth: c.widthPx,
                        maxWidth: c.widthPx,
                        verticalAlign: 'bottom',
                        boxSizing: 'border-box',
                        top: HEADER_HEIGHT,
                      }}
                    >
                      {c.filterContent ?? null}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>

            <TableBody>
              {rows.length > 0 ? (
                rows.map((row, idx) => {
                  const id = getRowId ? getRowId(row) : ((row as any).id ?? idx);
                  const isSelected = selectedRowId != null && id === selectedRowId;
                  return (
                    <TableRow
                      key={String(id)}
                      hover
                      onClick={() => onRowClick?.(row)}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        ...(isSelected ? { backgroundColor: '#e3f2fd !important' } : {}),
                        ...(getRowSx ? getRowSx(row) : {}),
                      }}
                    >
                      {visibleColumns.map((c) => (
                        <TableCell
                          key={c.key}
                          sx={{
                            ...bodyCellSx,
                            ...(isMobile
                              ? {
                                  color: '#263548',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontSize: 15,
                                  height: 60,
                                  lineHeight: '20px',
                                  padding: '10px',
                                }
                              : {}),
                            width: c.widthPx,
                            minWidth: c.widthPx,
                            maxWidth: c.widthPx,
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                          }}
                        >
                          {c.renderCell(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    sx={{ ...bodyCellSx, textAlign: 'center', color: '#6B7280', py: 3 }}
                  >
                    {error ? (
                      <Typography variant="body2" color="text.secondary">
                        See the notice above.
                      </Typography>
                    ) : loading ? (
                      <Typography variant="body2" color="text.secondary">
                        Loading…
                      </Typography>
                    ) : (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          display="block"
                          sx={{ fontWeight: 500 }}
                        >
                          {emptyMessage}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Try adjusting your filters.
                        </Typography>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(_, p) => onPageChange(p)}
          onRowsPerPageChange={(e) => onRowsPerPageChange?.(+e.target.value)}
          sx={{
            borderTop: '1px solid rgba(23, 36, 51, 0.12)',
            '& .MuiTablePagination-toolbar': {
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              ...(isMobile
                ? {
                    minHeight: 58,
                    px: 1,
                    gap: 0.5,
                  }
                : {}),
            },
            ...(isMobile
              ? {
                  '& .MuiTablePagination-spacer': { display: 'none' },
                  '& .MuiTablePagination-selectLabel': {
                    color: 'rgba(13, 32, 53, 0.6)',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    lineHeight: '16px',
                  },
                  '& .MuiTablePagination-input': { mr: 1 },
                  '& .MuiTablePagination-select': {
                    color: '#263548',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    lineHeight: '16px',
                    py: 0,
                  },
                  '& .MuiTablePagination-displayedRows': {
                    color: '#263548',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    lineHeight: '16px',
                    ml: 'auto',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTablePagination-actions': { ml: 1 },
                  '& .MuiIconButton-root': {
                    color: '#172433',
                    height: 32,
                    p: 0,
                    width: 32,
                  },
                }
              : {}),
            '& p': { marginBottom: 0 },
          }}
        />
      </Paper>

      {loading && rows.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 1, color: '#6B7280', fontSize: 12 }}>Loading…</Box>
      )}
    </Box>
  );
}

export default CADataGrid;
